import { supabase } from './supabaseClient';

export interface Discount {
  id: number;
  code: string;
  discount_percentage: number | null;
  fixed_discount: number | null;
  expiry_date: string;
  status: 'active' | 'expired';
}

export interface DiscountValidationResult {
  isValid: boolean;
  discount?: Discount;
  error?: string;
  amount?: number;
}

export const validateDiscount = async (code: string, totalAmount: number): Promise<DiscountValidationResult> => {
  try {
    const { data: discount, error } = await supabase
      .from('discounts')
      .select('*')
      .eq('code', code)
      .eq('status', 'active')
      .single();

    if (error) {
      return { isValid: false, error: 'Invalid discount code' };
    }

    if (!discount) {
      return { isValid: false, error: 'Discount code not found' };
    }

    // Check expiry
    const today = new Date();
    const expiryDate = new Date(discount.expiry_date);
    if (expiryDate < today) {
      // Update status to expired
      await supabase
        .from('discounts')
        .update({ status: 'expired' })
        .eq('id', discount.id);
      
      return { isValid: false, error: 'Discount code has expired' };
    }

    // Calculate discount amount
    let discountAmount = 0;
    if (discount.fixed_discount) {
      discountAmount = discount.fixed_discount;
    } else if (discount.discount_percentage) {
      discountAmount = (totalAmount * discount.discount_percentage) / 100;
    }

    return {
      isValid: true,
      discount: discount as Discount,
      amount: discountAmount
    };
  } catch (error) {
    console.error('Error validating discount:', error);
    return { isValid: false, error: 'Error validating discount code' };
  }
};

export const createDiscount = async (discountData: Omit<Discount, 'id'>): Promise<{ success: boolean; error?: string }> => {
  try {
    if (discountData.discount_percentage && discountData.fixed_discount) {
      return { success: false, error: 'Cannot set both percentage and fixed discount' };
    }

    const { error } = await supabase
      .from('discounts')
      .insert([discountData]);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error creating discount:', error);
    return { success: false, error: error.message };
  }
};

export const updateDiscountStatus = async (id: number, status: 'active' | 'expired'): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('discounts')
      .update({ status })
      .eq('id', id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error updating discount status:', error);
    return { success: false, error: error.message };
  }
};